//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement } from 'react'

import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import { PoolDynamicOverviewCTO, PoolFlexOverviewCTO, PoolOverviewCTO } from '@shared/sharedTypes'

import ViewLogo from '@react/components/elements/ViewLogo'
import { useOtSelector } from '@react/redux/store'

import { PoolTypeEnum } from '@shared/Enums'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { useNavigate } from 'react-router-dom'

import PoolCreateButtonMenu from './PoolCreate/PoolCreateButton'

import { buildPills } from '@react/components/elements/InterestPills'
import PoolListSkeleton from '@react/components/pools/PoolListSkeleton'
import { buildCompletePoolSummary } from '@react/lender/lenderUtils'
import { logOT } from '@shared/loggingShared'

function AdminPoolRow(props: {
  poolCTO: PoolFlexOverviewCTO | PoolOverviewCTO | PoolDynamicOverviewCTO
}): ReactElement {
  const { poolCTO } = props

  const theme = useTheme()
  const navigate = useNavigate()
  let interestPills: React.ReactElement | undefined = undefined

  if (poolCTO.poolType === PoolTypeEnum.Term) {
    const investorSummary = buildCompletePoolSummary(poolCTO as PoolOverviewCTO)
    interestPills = buildPills({ interestRate: `${investorSummary.finalizedApr}` }, theme)
  } else {
    interestPills = buildPills(poolCTO as PoolFlexOverviewCTO, theme)
  }

  if (!poolCTO) {
    return <></>
  }

  return (
    <TableRow key={poolCTO.poolAddr}>
      <TableCell
        key={'logo'}
        onClick={(_) => {
          navigate(`/pools/${poolCTO.poolAddr}`)
        }}
      >
        <ViewLogo style={{ width: '30px', height: '30px' }} poolAddr={poolCTO.poolAddr} />
      </TableCell>
      <TableCell
        key={'name'}
        align="left"
        onClick={(_) => {
          navigate(`/pools/${poolCTO.poolAddr}`)
        }}
      >
        <Typography alignContent={'left'} variant="bodyBold" gutterBottom component="div">
          {poolCTO.name}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>{interestPills}</Box>
      </TableCell>
      <TableCell
        key={'right'}
        align="right"
        onClick={(_) => {
          navigate(`/pools/${poolCTO.poolAddr}`)
        }}
      >
        <Typography display={'inline'} variant={'caption'}>
          {erc20WeiToCommaDecString(poolCTO.totalOutstandingLoanPrincipal)}/
        </Typography>

        <Typography display={'inline'} variant={'caption'}>
          {' '}
          {poolCTO.liquidityTokenSymbol}{' '}
        </Typography>
      </TableCell>
      <TableCell key={'nav'} align="right"></TableCell>
    </TableRow>
  )
}

export default function AdminPoolList(): ReactElement {
  const { isInitialized, poolOverviewCTOs } = useOtSelector((state) => state.poolState)
  const chainState = useOtSelector((state) => state.chainState)

  const theme = useTheme()
  const navigate = useNavigate()
  // const currentEthDate = Math.floor(Date.now() / 1e3)

  const poolCTOList = Object.values(poolOverviewCTOs).flat() as (
    | PoolOverviewCTO
    | PoolFlexOverviewCTO
    | PoolDynamicOverviewCTO
  )[]

  logOT.debug('poolCTOList LenderPoolOpenings', poolOverviewCTOs, poolCTOList, poolCTOList.length)
  if (!isInitialized) {
    return <PoolListSkeleton />
  }
  if (poolCTOList.length === 0) {
    return (
      <>
        <Typography align="left" sx={{ fontWeight: 'bold' }} variant={'h4'}>
          Available loan vaults
        </Typography>
        {chainState.isLoggedIn && <PoolCreateButtonMenu />}
        {chainState.isLoggedIn && (
          <Typography variant="h4" gutterBottom component="div">
            No Openings
          </Typography>
        )}
        {!chainState.isLoggedIn && (
          <Typography variant="h4" gutterBottom component="div">
            Connect to see loan vaults
          </Typography>
        )}
      </>
    )
  }
  return (
    <Paper
      data-testid="AdminPoolList"
      sx={{
        marginTop: 4,
        marginBottom: 4,
        padding: '24px',
        borderRadius: 4,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography align="left" sx={{ display: 'inline', float: 'left', fontWeight: 'bold' }} variant={'h4'}>
          Available loan vaults
        </Typography>
        {chainState.isLoggedIn && <PoolCreateButtonMenu />}
      </Box>

      <TableContainer sx={{ maxHeight: 800 }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: '5%' }}></TableCell>
              <TableCell style={{ width: '45%' }} align="right"></TableCell>
              <TableCell style={{ width: '43%' }} align="left"></TableCell>
              <TableCell style={{ width: '2%' }} align="left"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {poolCTOList.map((poolCTO: PoolOverviewCTO | PoolFlexOverviewCTO | PoolDynamicOverviewCTO) => {
              return <AdminPoolRow key={poolCTO.poolAddr} poolCTO={poolCTO} />
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ height: '60px', display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          align="left"
          sx={{ justifyContent: 'space-between', display: 'inline', float: 'left', fontWeight: 'bold' }}
          variant={'h4'}
        >
          Lender Explorer
        </Typography>
        <Button
          onClick={() => {
            navigate('/lenders')
          }}
        >
          View Full Page Lender Explorer
        </Button>
      </Box>
      {/* <LenderExplorerPage /> */}
    </Paper>
  )
}
